// src/components/admin/BusinessHoursEditor.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { updateSalonConfig } from '../../api/salonConfigService';
import { useSalonLocations } from '../../hooks/useSalonLocations';
import '../../styles/BusinessHours.css';

// ─── Time utilities ────────────────────────────────────────────────────────────

function formatTime(time24) {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return '';
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
}

function convert12To24(time12, period) {
  if (!time12) return '00:00';
  let [hours, minutes] = time12.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return '00:00';
  if (period === 'PM' && hours < 12) hours += 12;
  else if (period === 'AM' && hours === 12) hours = 0;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

// ─── Singapore holidays ────────────────────────────────────────────────────────

async function fetchSingaporeHolidays(year = new Date().getFullYear()) {
  try {
    const response = await fetch(`https://date.nager.at/api/v3/PublicHolidays/${year}/SG`);
    if (!response.ok) throw new Error('Failed to fetch holidays');
    const holidays = await response.json();
    return holidays.map(h => ({ date: h.date, name: h.name, localName: h.localName || h.name }));
  } catch {
    return getFallbackSingaporeHolidays(year);
  }
}

function getFallbackSingaporeHolidays(year) {
  return [
    { date: `${year}-01-01`, name: "New Year's Day",    localName: "New Year's Day" },
    { date: `${year}-02-10`, name: "Chinese New Year",  localName: "Chinese New Year" },
    { date: `${year}-02-11`, name: "Chinese New Year",  localName: "Chinese New Year (Day 2)" },
    { date: `${year}-03-29`, name: "Good Friday",       localName: "Good Friday" },
    { date: `${year}-05-01`, name: "Labour Day",        localName: "Labour Day" },
    { date: `${year}-05-22`, name: "Vesak Day",         localName: "Vesak Day" },
    { date: `${year}-06-17`, name: "Hari Raya Puasa",   localName: "Hari Raya Puasa" },
    { date: `${year}-08-09`, name: "National Day",      localName: "National Day" },
    { date: `${year}-08-24`, name: "Hari Raya Haji",    localName: "Hari Raya Haji" },
    { date: `${year}-10-24`, name: "Deepavali",         localName: "Deepavali" },
    { date: `${year}-12-25`, name: "Christmas Day",     localName: "Christmas Day" },
  ];
}

// ─── Hours helpers ─────────────────────────────────────────────────────────────

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const DEFAULT_HOURS_TEMPLATE = [
  { day: 'Monday',    open: '09:00', close: '18:00', isClosed: false },
  { day: 'Tuesday',   open: '09:00', close: '18:00', isClosed: false },
  { day: 'Wednesday', open: '09:00', close: '18:00', isClosed: false },
  { day: 'Thursday',  open: '09:00', close: '18:00', isClosed: false },
  { day: 'Friday',    open: '09:00', close: '18:00', isClosed: false },
  { day: 'Saturday',  open: '10:00', close: '16:00', isClosed: false },
  { day: 'Sunday',    open: '00:00', close: '00:00', isClosed: true  },
];

function normalizeRegularHours(raw = []) {
  return DAYS_OF_WEEK.map(day => {
    const existing = raw.find(h => h.day === day);
    return existing || { ...DEFAULT_HOURS_TEMPLATE.find(h => h.day === day) };
  });
}

function hoursToTimeInputs(regularHours) {
  return regularHours.map(day => ({
    openHours:    parseInt(day.open.split(':')[0]) % 12 || 12,
    openMinutes:  parseInt(day.open.split(':')[1]),
    openPeriod:   parseInt(day.open.split(':')[0]) >= 12 ? 'PM' : 'AM',
    closeHours:   parseInt(day.close.split(':')[0]) % 12 || 12,
    closeMinutes: parseInt(day.close.split(':')[1]),
    closePeriod:  parseInt(day.close.split(':')[0]) >= 12 ? 'PM' : 'AM',
  }));
}

// ─── Component ────────────────────────────────────────────────────────────────

function BusinessHoursEditor({ salonId, onPreview }) {
  const { locations: salonLocations, loading: locLoading } = useSalonLocations(salonId);
  // Salons with no `locations` array (e.g. single-location salons) get one implicit location,
  // otherwise the load effect below never runs and the editor stays on "Loading...".
  const locations = useMemo(
    () => (salonLocations.length > 0 ? salonLocations : [{ locationId: 'main', locationName: 'Main' }]),
    [salonLocations]
  );
  const isMultiLocation = locations.length > 1;

  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [saveMessage, setSaveMessage]   = useState({ text: '', isError: false });
  const [activeSection, setActiveSection] = useState('regular');
  const [editingClosureId, setEditingClosureId]       = useState(null);
  const [editingClosureLocId, setEditingClosureLocId] = useState(null);
  const [showAddForm, setShowAddForm]                 = useState(false);
  const [newClosure, setNewClosure]                   = useState(null);

  // Org-wide state
  const [sameHoursAllLocations, setSameHoursAllLocations] = useState(false);
  const [holidayClosures, setHolidayClosures]             = useState([]);

  // Per-location state: { [locationId]: { regularHours: [], customClosures: [] } }
  const [locationHoursMap, setLocationHoursMap] = useState({});
  // Parallel 12-hour display state: { [locationId]: [] }
  const [timeInputsMap, setTimeInputsMap]       = useState({});

  // Which location tab is selected (regular hours + custom closures)
  const [selectedLocationId, setSelectedLocationId] = useState('');

  // Singapore holidays
  const [singaporeHolidays, setSingaporeHolidays] = useState([]);
  const [loadingHolidays, setLoadingHolidays]     = useState(true);

  useEffect(() => {
    fetchSingaporeHolidays()
      .then(setSingaporeHolidays)
      .finally(() => setLoadingHolidays(false));
  }, []);

  // ── Load from Firestore ────────────────────────────────────────────────────
  useEffect(() => {
    if (!salonId || locLoading) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const snap = await getDoc(doc(db, 'salons', salonId));
        const data = snap.exists() ? snap.data() : {};
        const bh   = data.businessHours || {};

        const hc = bh.holidayClosures || [];
        const newMap    = {};
        const newTiMap  = {};

        if (bh.locations && typeof bh.locations === 'object') {
          // New multi-location format
          for (const loc of locations) {
            const locData = bh.locations[loc.locationId] || {};
            const rh = normalizeRegularHours(locData.regularHours || []);
            newMap[loc.locationId]   = { regularHours: rh, customClosures: locData.customClosures || [] };
            newTiMap[loc.locationId] = hoursToTimeInputs(rh);
          }
        } else {
          // Legacy flat format — seed first location, defaults for rest
          const firstId = locations[0].locationId;
          for (const loc of locations) {
            const rh = loc.locationId === firstId
              ? normalizeRegularHours(bh.regularHours || [])
              : normalizeRegularHours([]);
            const cc = loc.locationId === firstId ? (bh.customClosures || []) : [];
            newMap[loc.locationId]   = { regularHours: rh, customClosures: cc };
            newTiMap[loc.locationId] = hoursToTimeInputs(rh);
          }
        }

        if (!cancelled) {
          setHolidayClosures(hc);
          setSameHoursAllLocations(bh.sameHoursAllLocations || false);
          setLocationHoursMap(newMap);
          setTimeInputsMap(newTiMap);
          setSelectedLocationId(locations[0].locationId);
        }
      } catch (err) {
        console.error('[BusinessHoursEditor] Load error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [salonId, locLoading, locations]);

  // ── Derived active-location data ────────────────────────────────────────────
  // When sameHours is ON, always read/write through first location
  const editLocId       = sameHoursAllLocations ? (locations[0]?.locationId || '') : selectedLocationId;
  const activeRegHours  = locationHoursMap[editLocId]?.regularHours  || normalizeRegularHours([]);
  const activeTimeInputs = timeInputsMap[editLocId]  || hoursToTimeInputs(activeRegHours);
  // Custom closures always per-location (use selectedLocationId, not editLocId)
  const activeCustomClosures = locationHoursMap[selectedLocationId]?.customClosures || [];

  // ── Regular hours handlers ──────────────────────────────────────────────────
  const applyRegHours = (updatedRh, updatedTi) => {
    if (sameHoursAllLocations) {
      const newMap = {}, newTiMap = {};
      for (const loc of locations) {
        newMap[loc.locationId]   = { ...(locationHoursMap[loc.locationId] || {}), regularHours: updatedRh };
        newTiMap[loc.locationId] = updatedTi || hoursToTimeInputs(updatedRh);
      }
      setLocationHoursMap(prev => ({ ...prev, ...newMap }));
      setTimeInputsMap(prev => ({ ...prev, ...newTiMap }));
    } else {
      setLocationHoursMap(prev => ({
        ...prev,
        [editLocId]: { ...(prev[editLocId] || {}), regularHours: updatedRh },
      }));
      setTimeInputsMap(prev => ({
        ...prev,
        [editLocId]: updatedTi || hoursToTimeInputs(updatedRh),
      }));
    }
  };

  const handleClosedToggle = (index) => {
    const updated = [...activeRegHours];
    updated[index] = { ...updated[index], isClosed: !updated[index].isClosed };
    applyRegHours(updated);
  };

  const handleTimeChange = (index, field, value) => {
    const updatedTi = [...activeTimeInputs];
    updatedTi[index] = { ...updatedTi[index], [field]: value };

    const updatedRh = [...activeRegHours];
    if (field.startsWith('open')) {
      const { openHours, openMinutes, openPeriod } = updatedTi[index];
      updatedRh[index] = { ...updatedRh[index], open: convert12To24(`${openHours}:${openMinutes}`, openPeriod) };
    } else {
      const { closeHours, closeMinutes, closePeriod } = updatedTi[index];
      updatedRh[index] = { ...updatedRh[index], close: convert12To24(`${closeHours}:${closeMinutes}`, closePeriod) };
    }

    applyRegHours(updatedRh, updatedTi);
  };

  // ── Same-hours toggle ───────────────────────────────────────────────────────
  const handleSameHoursToggle = (checked) => {
    setSameHoursAllLocations(checked);
    if (checked) {
      // Propagate current selected location's hours to all locations
      const sourceRh = locationHoursMap[selectedLocationId]?.regularHours || normalizeRegularHours([]);
      const newMap = {}, newTiMap = {};
      for (const loc of locations) {
        newMap[loc.locationId]   = { ...(locationHoursMap[loc.locationId] || {}), regularHours: [...sourceRh] };
        newTiMap[loc.locationId] = hoursToTimeInputs(sourceRh);
      }
      setLocationHoursMap(prev => ({ ...prev, ...newMap }));
      setTimeInputsMap(prev => ({ ...prev, ...newTiMap }));
    }
  };

  // ── Holiday handlers (org-wide) ─────────────────────────────────────────────
  const handleHolidayToggle = (date, name) => {
    setHolidayClosures(prev =>
      prev.some(h => h.date === date)
        ? prev.filter(h => h.date !== date)
        : [...prev, { date, name, isClosed: true }]
    );
  };

  // ── Custom closure handlers (per-location) ──────────────────────────────────
  const addCustomClosure = () => {
    const today = new Date().toISOString().split('T')[0];
    setNewClosure({
      id: Date.now().toString(),
      startDate: today, endDate: today,
      startTime: '09:00', endTime: '18:00',
      isFullDay: true, reason: '', notes: '',
    });
    setShowAddForm(true);
  };

  const confirmAddClosure = () => {
    if (!newClosure) return;
    setLocationHoursMap(prev => ({
      ...prev,
      [selectedLocationId]: {
        ...(prev[selectedLocationId] || {}),
        customClosures: [...(prev[selectedLocationId]?.customClosures || []), newClosure],
      },
    }));
    setShowAddForm(false);
    setNewClosure(null);
  };

  const cancelAddClosure = () => {
    setShowAddForm(false);
    setNewClosure(null);
  };

  const updateCustomClosure = (closureId, field, value) => {
    setLocationHoursMap(prev => ({
      ...prev,
      [selectedLocationId]: {
        ...prev[selectedLocationId],
        customClosures: prev[selectedLocationId].customClosures.map(c =>
          c.id === closureId ? { ...c, [field]: value } : c
        ),
      },
    }));
  };

  const removeCustomClosure = (closureId) => {
    setLocationHoursMap(prev => ({
      ...prev,
      [selectedLocationId]: {
        ...prev[selectedLocationId],
        customClosures: prev[selectedLocationId].customClosures.filter(c => c.id !== closureId),
      },
    }));
  };

  // ── Location-aware closure helpers (used by table edit form) ───────────────
  const updateClosureInLocation = (locId, closureId, field, value) => {
    setLocationHoursMap(prev => ({
      ...prev,
      [locId]: {
        ...prev[locId],
        customClosures: (prev[locId]?.customClosures || []).map(c =>
          c.id === closureId ? { ...c, [field]: value } : c
        ),
      },
    }));
  };

  const removeClosureFromLocation = (locId, closureId) => {
    setLocationHoursMap(prev => ({
      ...prev,
      [locId]: {
        ...prev[locId],
        customClosures: (prev[locId]?.customClosures || []).filter(c => c.id !== closureId),
      },
    }));
    if (editingClosureId === closureId) {
      setEditingClosureId(null);
      setEditingClosureLocId(null);
    }
  };

  // ── Table helpers ──────────────────────────────────────────────────────────
  const getAllClosures = () => {
    const all = [];
    for (const loc of locations) {
      const locClosures = locationHoursMap[loc.locationId]?.customClosures || [];
      for (const c of locClosures) {
        all.push({ ...c, locationId: loc.locationId, locationName: loc.locationName });
      }
    }
    return all.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
  };

  const isFutureClosure = (closure) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(closure.endDate) >= today;
  };

  const formatClosureDate = (dateStr) => {
    if (!dateStr) return '—';
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-SG', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  };

  // ── Save ────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!salonId) return;
    setSaving(true);
    setSaveMessage({ text: '', isError: false });
    try {
      const locationsPayload = {};
      for (const [locId, data] of Object.entries(locationHoursMap)) {
        locationsPayload[locId] = {
          regularHours:   data.regularHours,
          customClosures: data.customClosures,
        };
      }
      const firstLocId = locations[0]?.locationId;
      const payload = {
        sameHoursAllLocations,
        holidayClosures,
        locations: locationsPayload,
        // Legacy top-level keys so old readers still work
        regularHours:   locationHoursMap[firstLocId]?.regularHours   || [],
        customClosures: locationHoursMap[firstLocId]?.customClosures || [],
      };
      await updateSalonConfig(salonId, { businessHours: payload });
      setSaveMessage({ text: 'Changes saved successfully!', isError: false });
    } catch (err) {
      console.error('[BusinessHoursEditor] Save error:', err);
      setSaveMessage({ text: 'Failed to save changes. Please try again.', isError: true });
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMessage({ text: '', isError: false }), 4000);
    }
  };

  // ── Render helpers ──────────────────────────────────────────────────────────
  const hoursOptions   = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutesOptions = [0, 15, 30, 45];

  const renderLocationTabs = (label) => (
    <div className="bh-location-tabs">
      {label && <span className="bh-location-tabs-label">{label}</span>}
      {locations.map(loc => (
        <button
          key={loc.locationId}
          className={`bh-location-tab${selectedLocationId === loc.locationId ? ' bh-location-tab--active' : ''}`}
          onClick={() => setSelectedLocationId(loc.locationId)}
        >
          {loc.locationName}
        </button>
      ))}
    </div>
  );

  const renderSectionNav = () => (
    <div className="section-nav">
      {['regular', 'holidays', 'custom'].map(section => (
        <button
          key={section}
          className={`nav-button ${activeSection === section ? 'active' : ''}`}
          onClick={() => setActiveSection(section)}
        >
          {section === 'regular' ? 'Regular Hours' : section === 'holidays' ? 'Holiday Closures' : 'Custom Closures'}
        </button>
      ))}
    </div>
  );

  const renderRegularHours = () => (
    <div className="regular-hours-section">
      <div className="hours-description">
        <p>Set your salon's standard operating hours for each day of the week.</p>
      </div>

      {isMultiLocation && (
        <div className="bh-same-hours-row">
          <label className="bh-same-hours-label">
            <input
              type="checkbox"
              checked={sameHoursAllLocations}
              onChange={e => handleSameHoursToggle(e.target.checked)}
            />
            <span>Same hours across all locations</span>
          </label>
          {sameHoursAllLocations && (
            <span className="bh-same-hours-hint">
              Editing hours here updates all locations simultaneously.
            </span>
          )}
        </div>
      )}

      {isMultiLocation && !sameHoursAllLocations && renderLocationTabs('Edit hours for:')}

      <div className="hours-table">
        <div className="hours-header">
          <div className="day-column">Day</div>
          <div className="hours-column">Hours</div>
          <div className="closed-column">Closed</div>
        </div>

        {activeRegHours.map((dayHours, index) => (
          <div key={dayHours.day} className="hours-row">
            <div className="day-column">
              {dayHours.day} <span className="required-asterisk">*</span>
            </div>

            <div className="hours-column">
              {dayHours.isClosed ? (
                <span className="closed-text">Closed</span>
              ) : (
                <div className="time-pickers">
                  <div className="time-picker">
                    <select
                      value={activeTimeInputs[index]?.openHours || 9}
                      onChange={e => handleTimeChange(index, 'openHours', parseInt(e.target.value))}
                    >
                      {hoursOptions.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                    :
                    <select
                      value={activeTimeInputs[index]?.openMinutes ?? 0}
                      onChange={e => handleTimeChange(index, 'openMinutes', parseInt(e.target.value))}
                    >
                      {minutesOptions.map(m => (
                        <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>
                      ))}
                    </select>
                    <select
                      value={activeTimeInputs[index]?.openPeriod || 'AM'}
                      onChange={e => handleTimeChange(index, 'openPeriod', e.target.value)}
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>

                  <span className="time-separator">to</span>

                  <div className="time-picker">
                    <select
                      value={activeTimeInputs[index]?.closeHours || 6}
                      onChange={e => handleTimeChange(index, 'closeHours', parseInt(e.target.value))}
                    >
                      {hoursOptions.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                    :
                    <select
                      value={activeTimeInputs[index]?.closeMinutes ?? 0}
                      onChange={e => handleTimeChange(index, 'closeMinutes', parseInt(e.target.value))}
                    >
                      {minutesOptions.map(m => (
                        <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>
                      ))}
                    </select>
                    <select
                      value={activeTimeInputs[index]?.closePeriod || 'PM'}
                      onChange={e => handleTimeChange(index, 'closePeriod', e.target.value)}
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            <div className="closed-column">
              <label className="checkbox-container">
                <input
                  type="checkbox"
                  checked={dayHours.isClosed}
                  onChange={() => handleClosedToggle(index)}
                />
                <span className="checkmark"></span>
              </label>
            </div>
          </div>
        ))}
      </div>

      <div className="hours-note">
        <p>Note: Check the "Closed" box for days when your salon is not open.</p>
      </div>
    </div>
  );

  const renderHolidayClosures = () => (
    <div className="holiday-closures-section">
      <div className="section-description">
        <p>Select which Singapore public holidays your salon will be closed. These apply to all locations.</p>
      </div>

      {loadingHolidays ? (
        <div className="loading-holidays">Loading Singapore public holidays...</div>
      ) : (
        <div className="holidays-list">
          {singaporeHolidays.map(holiday => {
            const isSelected = holidayClosures.some(h => h.date === holiday.date);
            return (
              <div key={holiday.date} className="holiday-item">
                <label className="holiday-checkbox">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleHolidayToggle(holiday.date, holiday.name)}
                  />
                  <span className="holiday-info">
                    <span className="holiday-name">{holiday.localName}</span>
                    <span className="holiday-date">
                      {new Date(holiday.date).toLocaleDateString('en-SG', {
                        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
                      })}
                    </span>
                  </span>
                </label>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderCustomClosures = () => {
    const allClosures = getAllClosures();

    const renderEditForm = (closure, locId) => {
      const live = locationHoursMap[locId]?.customClosures?.find(c => c.id === closure.id) || closure;
      return (
        <tr className="cc-edit-row">
          <td colSpan={isMultiLocation ? 7 : 6}>
            <div className="cc-edit-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date</label>
                  <input type="date" value={live.startDate}
                    onChange={e => updateClosureInLocation(locId, live.id, 'startDate', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input type="date" value={live.endDate}
                    onChange={e => updateClosureInLocation(locId, live.id, 'endDate', e.target.value)} />
                </div>
                <div className="form-group" style={{ justifyContent: 'flex-end' }}>
                  <label className="checkbox-label" style={{ marginTop: 28 }}>
                    <input type="checkbox" checked={live.isFullDay}
                      onChange={e => updateClosureInLocation(locId, live.id, 'isFullDay', e.target.checked)} />
                    Full Day Closure
                  </label>
                </div>
              </div>

              {!live.isFullDay && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Start Time</label>
                    <input type="time" value={live.startTime}
                      onChange={e => updateClosureInLocation(locId, live.id, 'startTime', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>End Time</label>
                    <input type="time" value={live.endTime}
                      onChange={e => updateClosureInLocation(locId, live.id, 'endTime', e.target.value)} />
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="form-group full-width">
                  <label>Reason</label>
                  <input type="text" value={live.reason}
                    onChange={e => updateClosureInLocation(locId, live.id, 'reason', e.target.value)}
                    placeholder="e.g., Renovation, Staff Training, Vacation" />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group full-width">
                  <label>Notes (Optional)</label>
                  <textarea value={live.notes}
                    onChange={e => updateClosureInLocation(locId, live.id, 'notes', e.target.value)}
                    placeholder="Additional details about this closure..."
                    rows="2" />
                </div>
              </div>

              <div className="cc-edit-footer">
                <button className="remove-closure-button"
                  onClick={() => removeClosureFromLocation(locId, live.id)}>
                  Remove
                </button>
                <button className="cc-done-btn"
                  onClick={() => { setEditingClosureId(null); setEditingClosureLocId(null); }}>
                  Done
                </button>
              </div>
            </div>
          </td>
        </tr>
      );
    };

    return (
      <div className="custom-closures-section">
        <div className="section-header">
          <div className="section-description">
            <p>Manage one-off closure periods for renovations, staff events, or special circumstances. Future closures can be edited; past closures are read-only.</p>
          </div>
          <button className="add-closure-button" onClick={addCustomClosure}>
            + Add Closure
          </button>
        </div>

        {isMultiLocation && (
          <div className="cc-add-location-row">
            <span className="cc-add-location-label">New closure will be added for:</span>
            {locations.map(loc => (
              <button
                key={loc.locationId}
                className={`bh-location-tab${selectedLocationId === loc.locationId ? ' bh-location-tab--active' : ''}`}
                onClick={() => setSelectedLocationId(loc.locationId)}
              >
                {loc.locationName}
              </button>
            ))}
          </div>
        )}

        {/* Standalone add form — shown only when + Add Closure is clicked */}
        {showAddForm && newClosure && (
          <div className="cc-add-panel">
            <h4 className="cc-add-panel-title">New Closure{isMultiLocation ? ` — ${locations.find(l => l.locationId === selectedLocationId)?.locationName || ''}` : ''}</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Start Date</label>
                <input type="date" value={newClosure.startDate}
                  onChange={e => setNewClosure(p => ({ ...p, startDate: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>End Date</label>
                <input type="date" value={newClosure.endDate}
                  onChange={e => setNewClosure(p => ({ ...p, endDate: e.target.value }))} />
              </div>
              <div className="form-group" style={{ justifyContent: 'flex-end' }}>
                <label className="checkbox-label" style={{ marginTop: 28 }}>
                  <input type="checkbox" checked={newClosure.isFullDay}
                    onChange={e => setNewClosure(p => ({ ...p, isFullDay: e.target.checked }))} />
                  Full Day Closure
                </label>
              </div>
            </div>
            {!newClosure.isFullDay && (
              <div className="form-row">
                <div className="form-group">
                  <label>Start Time</label>
                  <input type="time" value={newClosure.startTime}
                    onChange={e => setNewClosure(p => ({ ...p, startTime: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>End Time</label>
                  <input type="time" value={newClosure.endTime}
                    onChange={e => setNewClosure(p => ({ ...p, endTime: e.target.value }))} />
                </div>
              </div>
            )}
            <div className="form-row">
              <div className="form-group full-width">
                <label>Reason</label>
                <input type="text" value={newClosure.reason}
                  onChange={e => setNewClosure(p => ({ ...p, reason: e.target.value }))}
                  placeholder="e.g., Renovation, Staff Training, Vacation" />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group full-width">
                <label>Notes (Optional)</label>
                <textarea value={newClosure.notes}
                  onChange={e => setNewClosure(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Additional details about this closure..."
                  rows="2" />
              </div>
            </div>
            <div className="cc-edit-footer">
              <button className="remove-closure-button" onClick={cancelAddClosure}>Cancel</button>
              <button className="cc-done-btn" onClick={confirmAddClosure}>Add Closure</button>
            </div>
          </div>
        )}

        {allClosures.length === 0 ? (
          <div className="no-closures">
            <p>No custom closure periods set. Click &quot;+ Add Closure&quot; to create one.</p>
          </div>
        ) : (
          <div className="cc-table-wrapper">
            <table className="cc-table">
              <thead>
                <tr>
                  {isMultiLocation && <th>Location</th>}
                  <th className="cc-th-start">Start</th>
                  <th className="cc-th-end">End</th>
                  <th>Type</th>
                  <th>Reason</th>
                  <th className="cc-th-status">Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {allClosures.map(closure => {
                  const future   = isFutureClosure(closure);
                  const isEditing = editingClosureId === closure.id && editingClosureLocId === closure.locationId;
                  return (
                    <React.Fragment key={`${closure.locationId}-${closure.id}`}>
                      <tr className={`cc-row${future ? '' : ' cc-row--past'}`}>
                        {isMultiLocation && <td className="cc-cell-loc">{closure.locationName}</td>}
                        <td>
                          <div className="cc-date">{formatClosureDate(closure.startDate)}</div>
                          {!closure.isFullDay && <div className="cc-time">{formatTime(closure.startTime)}</div>}
                        </td>
                        <td>
                          <div className="cc-date">{formatClosureDate(closure.endDate)}</div>
                          {!closure.isFullDay && <div className="cc-time">{formatTime(closure.endTime)}</div>}
                        </td>
                        <td>
                          {closure.isFullDay
                            ? 'Full Day'
                            : `${formatTime(closure.startTime)} – ${formatTime(closure.endTime)}`}
                        </td>
                        <td className="cc-cell-reason">{closure.reason || <span className="cc-empty">—</span>}</td>
                        <td>
                          {future
                            ? <span className="cc-badge cc-badge--future">Upcoming</span>
                            : <span className="cc-badge cc-badge--past">Past</span>}
                        </td>
                        <td className="cc-cell-actions">
                          {future && (
                            <button
                              className={`cc-edit-btn${isEditing ? ' cc-edit-btn--cancel' : ''}`}
                              onClick={() => {
                                if (isEditing) {
                                  setEditingClosureId(null);
                                  setEditingClosureLocId(null);
                                } else {
                                  setEditingClosureId(closure.id);
                                  setEditingClosureLocId(closure.locationId);
                                }
                              }}
                            >
                              {isEditing ? 'Cancel' : 'Edit'}
                            </button>
                          )}
                        </td>
                      </tr>
                      {isEditing && renderEditForm(closure, closure.locationId)}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ── Loading state ──────────────────────────────────────────────────────────
  if (loading || locLoading) {
    return (
      <div className="hours-editor">
        <div className="editor-page-header">
          <h2>Business Hours &amp; Closures</h2>
        </div>
        <div className="contact-loading">Loading business hours...</div>
      </div>
    );
  }

  return (
    <div className="hours-editor">
      <div className="editor-page-header">
        <h2>Business Hours &amp; Closures</h2>
        <div className="editor-page-actions">
          {saveMessage.text && (
            <span className={`editor-save-msg${saveMessage.isError ? ' error' : ''}`}>
              {saveMessage.text}
            </span>
          )}
          <button className="page-action-btn page-action-preview" type="button" onClick={onPreview}>
            Live Preview
          </button>
          <button
            className="page-action-btn page-action-save"
            type="button"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {renderSectionNav()}

      <div className="section-content">
        {activeSection === 'regular'  && renderRegularHours()}
        {activeSection === 'holidays' && renderHolidayClosures()}
        {activeSection === 'custom'   && renderCustomClosures()}
      </div>
    </div>
  );
}

export default BusinessHoursEditor;
